import Constants from './Constants';
import Utils from './Utils';

// A season is a six-week block of dailies. Everything here is pure date maths
// over "YYYY-MM-DD" strings, shared by the client and the server the same way
// Scoring.js is, so both sides agree on which season a date belongs to without
// a round trip.
//
// The epoch is a Monday and a season is 42 days, so every season starts on a
// Monday and spans exactly six of the Monday-anchored leaderboard weeks. That
// is the reason for six weeks rather than, say, a calendar month: a season is
// never a ragged number of weeks, and a week is never split across two seasons.
class Seasons {
  // Which season a date falls in, numbered from 1. Dates before the epoch
  // return 0, meaning "played before seasons existed"; callers show those
  // players their stars and nothing else rather than inventing a season for
  // history that was never played under one.
  static getSeasonNumber = (date) => {
    const daysIn = Utils.getDaysBetween(Constants.SEASON_EPOCH, date);
    if (daysIn < 0) {
      return 0;
    }

    return Math.floor(daysIn / Constants.SEASON_LENGTH_DAYS) + 1;
  }

  static getSeasonStartDate = (seasonNumber) => {
    return Utils.addDays(Constants.SEASON_EPOCH, (seasonNumber - 1) * Constants.SEASON_LENGTH_DAYS);
  }

  static getSeasonEndDate = (seasonNumber) => {
    return Utils.addDays(Seasons.getSeasonStartDate(seasonNumber), Constants.SEASON_LENGTH_DAYS - 1);
  }

  static getSeason = (seasonNumber) => {
    return {
      number: seasonNumber,
      startDate: Seasons.getSeasonStartDate(seasonNumber),
      endDate: Seasons.getSeasonEndDate(seasonNumber)
    };
  }

  static getCurrentSeasonNumber = () => {
    return Seasons.getSeasonNumber(Utils.getDateStringToday());
  }

  static getCurrentSeason = () => {
    return Seasons.getSeason(Seasons.getCurrentSeasonNumber());
  }

  // Which day of its season a date is, from 1 to SEASON_LENGTH_DAYS.
  static getDayInSeason = (date) => {
    const seasonNumber = Seasons.getSeasonNumber(date);
    if (seasonNumber === 0) {
      return 0;
    }

    return Utils.getDaysBetween(Seasons.getSeasonStartDate(seasonNumber), date) + 1;
  }

  // Days left in the season, counting the given date itself -- so the final
  // Sunday of a season reads as 1 day remaining, not 0.
  static getDaysRemaining = (date) => {
    const dayInSeason = Seasons.getDayInSeason(date);
    if (dayInSeason === 0) {
      return 0;
    }

    return Constants.SEASON_LENGTH_DAYS - dayInSeason + 1;
  }

  // Whether the season calendar has begun. Every piece of season UI hangs off
  // this: until the first season starts the site is meant to look exactly as it
  // did before seasons existed, including keeping the trophy on weekly wins,
  // which only moves up to seasons once there are seasons to award it for.
  static hasStarted = (date) => {
    return Seasons.getSeasonNumber(date) >= 1;
  }

  // Where a date sits on the season calendar: inside a season, or in the run-up
  // before the first one begins. Returned as data rather than as a sentence so
  // the wording lives with the components and the arithmetic stays testable at
  // any date, not just today's.
  static getSeasonStatus = (date) => {
    const number = Seasons.getSeasonNumber(date);

    if (number === 0) {
      return {
        started: false,
        number: 1,
        daysUntilStart: Utils.getDaysBetween(date, Seasons.getSeasonStartDate(1)),
        daysRemaining: 0
      };
    }

    return {
      started: true,
      number,
      daysUntilStart: 0,
      daysRemaining: Seasons.getDaysRemaining(date)
    };
  }

  // The week of daily parameters a date is played under. Dates before the
  // epoch keep the pre-season schedule; past the last authored season the most
  // recent one repeats, so a forgotten season degrades to a stale week rather
  // than to an undefined board.
  static getSchedule = (seasonNumber) => {
    if (seasonNumber < 1) {
      return Constants.LEGACY_SCHEDULE;
    }

    return Constants.SEASONS[Math.min(seasonNumber, Constants.SEASONS.length) - 1];
  }

  // Where a date sits in a Monday-first week, from 0 (Monday) to 6 (Sunday).
  // The schedule tables are laid out that way, and so are a season's per-day
  // bests; moment counts Sunday as 0, so this is the one place that conversion
  // happens.
  static getDayIndex = (date) => {
    return (Utils.getDayOfWeek(date) + 6) % 7;
  }

  // The parameters for one date's daily. The single source for them: the
  // server validates against these, and the client draws its board from them.
  static getDailyParameters = (date) => {
    const week = Seasons.getSchedule(Seasons.getSeasonNumber(date));
    const parameters = Object.assign(
      {},
      Constants.DAILY_DEFAULTS,
      week[Seasons.getDayIndex(date)]
    );

    // On increment days the options cycle 1-2-3, but the day's letters are
    // always dealt three to a round, so the deal is wider than the display.
    parameters.letterOptionCount = parameters.incrementMode
      ? Constants.MAX_LETTER_OPTIONS_PER_ROUND
      : parameters.letterOptionsPerRound;

    return parameters;
  }

  // How many of a round's dealt letters the day actually puts on offer. Every
  // day but an increment day offers the whole round; an increment day cycles
  // through 1, 2 and 3 from the day's own count, which is the entire mechanic.
  //
  // This is shared rather than the client's business because the deal is three
  // wide whatever is showing: the letters a round holds back are sitting in the
  // day's string, and a placement is only legal if it came from what the round
  // offered. The server reads this to decide that, so the rule cannot be kept
  // in the one place that is easiest to edit from a console.
  static getRoundLetterOptionCount = (parameters, round) => {
    if (!parameters.incrementMode) {
      return parameters.letterOptionsPerRound;
    }

    const cycle = Constants.MAX_LETTER_OPTIONS_PER_ROUND;

    return ((parameters.letterOptionsPerRound + round - 1) % cycle) + 1;
  }

  // How many rounds a day's board takes to fill: every cell, plus the second
  // slot of each expanded cell, less the letters that start on the board.
  static getRoundCount = (parameters) => {
    return (parameters.rows * parameters.columns) + parameters.expansionCount - parameters.startLetterCount;
  }

  // True once every day of the season is in the past, which is the point at
  // which its standings can be settled and its trophies awarded.
  static isSeasonComplete = (seasonNumber, today) => {
    return seasonNumber >= 1 && Seasons.getSeasonEndDate(seasonNumber) < today;
  }
}

export default Seasons;
